/**
 * @file index.tsx
 *
 * @fileoverview Main component entry point for the app. Will get basic information about the
 * user and other info that's essential to the app.
 */
import { ApolloClient } from '@apollo/client';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import dynamic from 'next/dynamic';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { GoogleAnalytics } from '~/jobiqo/components/GA4';
import { GTM } from '~/jobiqo/components/GTM';
import { AppContext } from '~/jobiqo/context/app';
import { HeaderContext } from '~/jobiqo/context/header';
import { LayoutProvider } from '~/jobiqo/context/layout';
import { StoreProvider } from '~/jobiqo/context/state';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { User } from '~/jobiqo/models/user';
import { consentEvent, getConsents } from '~/jobiqo/services/cmp';
import { Metatags } from '../Metatag';
import { useTestingLogger } from '~/jobiqo/hooks/useTestingLogger';
import CurrentOrganization from '~/jobiqo/components/Organization/CurrentOrganization';
import FooterScriptsSlot from '~/jobiqo/components/Slots/FooterScriptsSlot';
import StatuscakeCheck from '~/jobiqo/components/StatuscakeCheck';
import Snowplow from '~/jobiqo/components/Snowplow';
import MetaPixel from '~/jobiqo/components/MetaPixel';
import Oewa from '~/jobiqo/components/Oewa/OewaScript';
import Webtrekk from '~/jobiqo/components/Webtrekk';
import Hotjar from '~/jobiqo/components/Hotjar';
import GdprConsentModal from '~/jobiqo/components/GDPRConsent/components/Modal';
import SiteOrganizationSchema from '~/jobiqo/components/SchemaOrg/SiteOrganization';
import ServerSideTracking from '~/jobiqo/components/ServerSideTracking';

const Flags = dynamic(() => import('~/jobiqo/components/Flags'), {
  ssr: false,
});

const CMP = dynamic(() => import('~/jobiqo/components/CMP'), {
  ssr: false,
});

const BrowserTabNotification = dynamic(
  () => import('~/jobiqo/components/BrowserTabNotification'),
  {
    ssr: false,
  },
);

interface AppProps {
  /**
   * The apollo client.
   */
  apolloClient: ApolloClient<any>;
  /**
   * The component being rendered.
   */
  Component: any;
  /**
   * The current user.
   */
  user: User;
  /**
   * Information about if the user is authenticated.
   */
  authenticated: boolean;
  /**
   * Extra props passed by the current page.
   */
  pageProps: any;
  /**
   * The locale selected.
   */
  dateFormats: string;
  /**
   * The current region.
   */
  locale: string;
  /**
   * The page slug.
   */
  path: string;
  /**
   * The current host.
   */
  host: string;
}

const JobiqoAppComponent = ({
  apolloClient,
  Component,
  user,
  authenticated,
  dateFormats,
  locale,
  pageProps,
  path,
  host,
}: AppProps) => {
  // Used to provide relevant work information for Behat tests.
  useTestingLogger();

  const { setConsents }: UserContextInterface = useContext(UserContext);
  const { APP, JOBS } = useFrontendConfig();
  const SITENAME = APP.SITENAME;
  const JOBS_PAGE = JOBS.JOBS_PAGE;
  const GDPR_CONSENT_LATEST_TC_ACTIVE = APP.GDPR_CONSENT_LATEST_TC_ACTIVE;

  const [headerLeftButton, setHeaderLeftButton] = useState<React.ReactNode>(
    null,
  );
  const [headerRightButton, setHeaderRightButton] = useState<React.ReactNode>(
    null,
  );
  const [_, startTransition] = useTransition();

  const handleConsent = (event) => {
    startTransition(() => {
      setConsents(getConsents(event));
    });
  };

  const consentEventName = useMemo(() => consentEvent(), []);

  const testing = (process.env.NODE_TEST as string) === 'testing';
  const development = (process.env.NODE_ENV as string) === 'development';

  useEffect(() => {
    window.addEventListener(consentEventName, handleConsent);
    return () => window.removeEventListener(consentEventName, handleConsent);
  }, [consentEventName]);

  const appContextValue = useMemo(
    () => ({
      searchLink: JOBS_PAGE,
      sitename: SITENAME,
      dateFormats,
      locale,
    }),
    [dateFormats, locale],
  );

  return (
    <AppContext.Provider value={appContextValue}>
      <LayoutProvider>
        <HeaderContext.Provider
          value={{
            leftButton: headerLeftButton,
            rightButton: headerRightButton,
            setLeftButton: (component) => {
              startTransition(() => {
                setHeaderLeftButton(component);
              });
            },
            setRightButton: (component) => {
              startTransition(() => {
                setHeaderRightButton(component);
              });
            },
          }}
        >
          <StoreProvider>
            <StatuscakeCheck />
            <Metatags path={path} host={host} />
            <Flags />
            <CMP />
            <GTM />
            <GoogleAnalytics />
            <Snowplow />
            <MetaPixel />
            <Oewa />
            <Hotjar />
            <CurrentOrganization />
            <BrowserTabNotification />
            <SiteOrganizationSchema />
            <ServerSideTracking
              processedPath={path}
              testing={testing}
              development={development}
            />
            {GDPR_CONSENT_LATEST_TC_ACTIVE && user && (
              <GdprConsentModal
                placement={'REGISTRATION'}
                type={'PRIVACY_POLICY'}
              />
            )}
            <Component
              client={apolloClient}
              authenticated={authenticated}
              user={user}
              path={path}
              {...pageProps}
            />
            <Webtrekk />
            <FooterScriptsSlot />
          </StoreProvider>
        </HeaderContext.Provider>
      </LayoutProvider>
    </AppContext.Provider>
  );
};
export const JobiqoApp = JobiqoAppComponent;
export default JobiqoApp;
